/**
 * Shared pure-math scene-geometry builder for the single-line `TEXT` entity
 * (DEFUSE Direction-3 — clone elimination).
 *
 * Takes the already-extracted primitive inputs (insertion `Vec3`, height,
 * rotation, width factor, oblique angle, style name, content string, the raw
 * justify KEYWORD, and whether an alignment point is present) and returns the
 * `{ geometry, bounds }` pair that both the legacy `textToRenderable.ts`
 * (reads the `EntityPreview.properties` string bag) and the typed
 * `typed/textFromEntity.ts` (reads the canonical `TextEntity` fields) feed
 * into `buildRenderableShell`.
 *
 * Why this module exists: the two adapters were byte-identical AFTER value
 * extraction — the SAME justify-keyword → `TextAlignment` mapping, the SAME
 * `applyOffset(insertion)`, the SAME `text` SceneGeometry assembly, and the
 * SAME conservative bbox. That is one semantic truth (the text render geometry
 * math), and it now lives here ONCE; each adapter is reduced to its value
 * extraction (which differs only in the SOURCE — string bag vs typed model)
 * plus a single builder call. This keeps the jscpd dup gate from seeing a
 * typed ↔ legacy clone.
 *
 * `applyOffset` is applied INSIDE the builder to the absolute insertion point —
 * exactly as the legacy adapter did. The justify-keyword → grid mapping
 * (`parseTextJustification`) mirrors AutoCAD's TEXT justification enum
 * (Autodesk DXF reference → TEXT group codes 72/73) and is exported so both
 * adapters reach the identical decision tree.
 *
 * Pure: no `EntityPreview` / `DrawingEntity` import, no side effects beyond
 * reading the locked-in coordinate origin via `applyOffset`.
 */

import { applyOffset } from "../../coordinateOrigin";
import type { SceneGeometry, TextAlignment, Vec3 } from "../../types";

import type { SceneGeometryResult } from "../sceneGeometryBuilders";

/**
 * Map AutoCAD's TEXT justify keyword onto our `TextAlignment` grid.
 *
 * Single home for the keyword → alignment decision tree shared by the legacy
 * bag adapter (`Justify` written by `justifyFromCodes`) and the typed adapter
 * (which derives the SAME keyword from `hJustification` / `vJustification`).
 * Behaviour is identical to the original inline `parseJustification` in
 * `textToRenderable.ts` — extracted verbatim, no semantic change.
 */
export function parseTextJustification(
  justify: string,
  hasAlignmentPoint: boolean,
): TextAlignment {
  const j = justify.trim().toUpperCase();
  if (j === "" || j === "LEFT" || j === "BASELINE") return "baseline-left";
  if (j === "CENTER") return "baseline-center";
  if (j === "RIGHT") return "baseline-right";
  if (j === "MIDDLE" || j === "MC") return "middle-center";
  if (j === "ALIGNED") return "aligned";
  if (j === "FIT") return "fit";

  // 9-cell compound grid (TL, TC, TR, ML, MC, MR, BL, BC, BR).
  let row: "top" | "middle" | "bottom" = "baseline" as never;
  let col: "left" | "center" | "right" = "left";
  if (j.startsWith("T")) row = "top";
  else if (j.startsWith("M")) row = "middle";
  else if (j.startsWith("B")) row = "bottom";

  if (j.includes("L")) col = "left";
  else if (j.includes("C")) col = "center";
  else if (j.includes("R")) col = "right";

  // Defensive fallback: if the keyword didn't carry a row hint at all,
  // resolve to baseline-X. This matches AutoCAD's behaviour when the
  // alignment point group codes are absent (no alignment point → use
  // baseline anchor).
  if ((row as string) === "baseline" || !hasAlignmentPoint) {
    if (col === "left") return "baseline-left";
    if (col === "center") return "baseline-center";
    return "baseline-right";
  }
  return `${row}-${col}` as TextAlignment;
}

/**
 * Build the `text` SceneGeometry + conservative AABB from the raw (pre-offset)
 * insertion point and the already-extracted text parameters. `applyOffset` is
 * applied to the insertion point (identical to the legacy adapter).
 *
 * The bbox is the SAME PR-E2 conservative square the legacy adapter emitted: a
 * rectangle centred on the insertion with half-width =
 * `max(1, height * max(widthFactor, 1) * max(content.length, 1) * 0.5)` and
 * half-height = `max(1, height)`, so spatial queries find the entity before
 * the run renderer measures its true extent.
 */
export function buildTextSceneGeometry(
  insertionRaw: Vec3,
  height: number,
  rotation: number,
  widthFactor: number,
  oblique: number,
  styleName: string,
  content: string,
  justify: string,
  hasAlignmentPoint: boolean,
  generationFlags = 0,
): SceneGeometryResult {
  const position = applyOffset(insertionRaw);
  const alignment = parseTextJustification(justify, hasAlignmentPoint);

  // Codex #342 F1 — DXF TEXT group-71 generation flags (Autodesk DXF reference):
  // bit 2 = BACKWARD (reflect reading direction across local Y), bit 4 =
  // UPSIDE_DOWN (flip up across local X). MIRRTEXT=1 + MIRROR toggles bit 4. The
  // live mesh path applies these as a local-axis flip; absent ⇒ no flip (golden
  // parity for non-mirrored text). Mirrors `renderShxText.tsx` mirrorX/mirrorY.
  const backward = (generationFlags & 2) !== 0;
  const upsideDown = (generationFlags & 4) !== 0;

  const geometry: SceneGeometry = {
    kind: "text",
    position,
    rotation,
    height,
    widthFactor,
    oblique,
    styleName,
    content,
    alignment,
    backward,
    upsideDown,
  };

  const halfX = Math.max(
    1,
    height * Math.max(widthFactor, 1) * Math.max(content.length, 1) * 0.5,
  );
  const halfY = Math.max(1, height);
  const bounds = {
    minX: position.x - halfX,
    minY: position.y - halfY,
    maxX: position.x + halfX,
    maxY: position.y + halfY,
  };

  return { geometry, bounds };
}
